import { ContainerClinicianTableQueryVariables } from '@/pages/DashboardPage/containers/ContainerClinicianTable.gql.types';
import { ContainerEpisodeTableQueryVariables } from '@/pages/DashboardPage/containers/ContainerEpisodeTable.queries.gql.types';
import { ContainerOrderTableQueryVariables } from '@/pages/DashboardPage/containers/ContainerOrderTable.queries.gql.types';
import { ContainerPatientTableQueryVariables } from '@/pages/DashboardPage/containers/ContainerPatientTable.queries.gql.types';
import { ContainerShiftBlockTableQueryVariables } from '@/pages/DashboardPage/containers/ContainerShiftBlockSyncIssuesTable.gql.types';
import { ContainerVisitRequestTableQueryVariables } from '@/pages/DashboardPage/containers/ContainerVisitRequestTable.gql.types';
import { ContainerVisitTableQueryVariables } from '@/pages/DashboardPage/containers/ContainerVisitTable.queries.gql.types';
import { Utils } from '@/types/utils';

import { ContainerVisitTableExtraProps } from './ContainerVisitTable';
import { ContainerDefinitionKey } from './definitions';
import { ContainerQueryOptions } from './queries';

export enum RootEntity {
    CLINICIAN = 'CLINICIAN',
    EPISODE_OF_CARE = 'EPISODE_OF_CARE',
    ORDER = 'ORDER',
    PATIENT = 'PATIENT',
    SHIFT_BLOCK = 'SHIFT_BLOCK',
    VISIT = 'VISIT',
    VISIT_REQUEST = 'VISIT_REQUEST',
}

type EntityTableTypes = {
    [RootEntity.CLINICIAN]: {
        entity: RootEntity.CLINICIAN;
    } & ContainerClinicianTableQueryVariables;
    [RootEntity.EPISODE_OF_CARE]: {
        entity: RootEntity.EPISODE_OF_CARE;
    } & ContainerEpisodeTableQueryVariables;
    [RootEntity.ORDER]: {
        entity: RootEntity.ORDER;
    } & ContainerOrderTableQueryVariables;
    [RootEntity.PATIENT]: {
        entity: RootEntity.PATIENT;
    } & ContainerPatientTableQueryVariables;
    [RootEntity.SHIFT_BLOCK]: {
        entity: RootEntity.SHIFT_BLOCK;
    } & ContainerShiftBlockTableQueryVariables;
    [RootEntity.VISIT]: {
        entity: RootEntity.VISIT;
    } & ContainerVisitTableQueryVariables;
    [RootEntity.VISIT_REQUEST]: {
        entity: RootEntity.VISIT_REQUEST;
    } & ContainerVisitRequestTableQueryVariables;
};
type EntityTableDefinition<EntityT extends RootEntity> = {
    entity: EntityT;
    filters: EntityTableTypes[EntityT]['filters'];
    ordering: EntityTableTypes[EntityT]['ordering'];
    rowToLink: ((row: Utils.UNSAFE_ANY) => string) | null;
    // Opt-in: when the HIGHLIGHT_LINKED_VISIT flag is on, row clicks go to the
    // patient scheduling tab anchored to the visit instead of following
    // `rowToLink`. The specific link builder (episode-scoped vs patient-scoped)
    // is chosen at render time based on the org's EPISODES_OF_CARE flag.
    highlightLinkedVisit?: boolean;
    emptyTableText?: string;
};

export type DashboardContainerDefinition<EntityT extends RootEntity, ExtraProps = object> = {
    label: string;
    key: ContainerDefinitionKey;
    hoverText?: string;
    tableConfig: EntityTableDefinition<EntityT>;
    // If true, separates out the count query for the container from the batched
    // query for other containers so that it does not delay the loading of the page.
    // When we upgrade django/strawberry/etc, we can use @defer directives
    // to achieve the same effect without having to separate out the queries.
    heavyQuery?: boolean;
    // If true, the container participates in the snooze feature (counts on the card, tabs, per-row CTAs).
    snoozable?: boolean;
    queryOptions?: ContainerQueryOptions;
    extraProps?: ExtraProps;
};

export type DashboardContainer<
    EntityT extends RootEntity,
    ExtraProps = object,
> = DashboardContainerDefinition<EntityT, ExtraProps> & {
    defaultEnabled: boolean;
};

export type ClinicianDashboardContainer = DashboardContainer<RootEntity.CLINICIAN>;
export type EpisodeOfCareDashboardContainer = DashboardContainer<RootEntity.EPISODE_OF_CARE>;
export type OrderDashboardContainer = DashboardContainer<RootEntity.ORDER>;
export type PatientDashboardContainer = DashboardContainer<RootEntity.PATIENT>;
export type ShiftBlockDashboardContainer = DashboardContainer<RootEntity.SHIFT_BLOCK>;
export type VisitDashboardContainer = DashboardContainer<
    RootEntity.VISIT,
    ContainerVisitTableExtraProps
>;
export type VisitRequestDashboardContainer = DashboardContainer<RootEntity.VISIT_REQUEST>;

export function isClinicianContainer(
    container: DashboardContainer<RootEntity>,
): container is ClinicianDashboardContainer {
    return container.tableConfig.entity === RootEntity.CLINICIAN;
}

export function isEpisodeOfCareContainer(
    container: DashboardContainer<RootEntity>,
): container is EpisodeOfCareDashboardContainer {
    return container.tableConfig.entity === RootEntity.EPISODE_OF_CARE;
}

export function isOrderContainer(
    container: DashboardContainer<RootEntity>,
): container is OrderDashboardContainer {
    return container.tableConfig.entity === RootEntity.ORDER;
}

export function isPatientContainer(
    container: DashboardContainer<RootEntity>,
): container is PatientDashboardContainer {
    return container.tableConfig.entity === RootEntity.PATIENT;
}

export function isVisitContainer(
    container: DashboardContainer<RootEntity, unknown>,
): container is VisitDashboardContainer {
    return container.tableConfig.entity === RootEntity.VISIT;
}

export function isShiftBlockContainer(
    container: DashboardContainer<RootEntity>,
): container is ShiftBlockDashboardContainer {
    return container.tableConfig.entity === RootEntity.SHIFT_BLOCK;
}

export function isVisitRequestContainer(
    container: DashboardContainer<RootEntity>,
): container is VisitRequestDashboardContainer {
    return container.tableConfig.entity === RootEntity.VISIT_REQUEST;
}
